import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { useModules } from '../hooks/useModules';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Save, LayoutGrid, BookOpen, Calendar, UserCheck, FileText, 
  ShoppingCart, Banknote, Receipt, Wallet, Building2, Users, 
  BarChart3, Undo2, Shield, Database, AlertTriangle, CheckCircle
} from 'lucide-react';

// Module definitions with icons and descriptions
const MODULE_DEFINITIONS = {
  reservations: {
    label: 'Réservations',
    icon: BookOpen,
    description: 'Gestion des réservations et du planning',
    critical: true
  },
  calendar: {
    label: 'Calendrier',
    icon: Calendar,
    description: 'Vue calendrier des réservations',
    depends: ['reservations']
  },
  precheckin: {
    label: 'Pré-checkin',
    icon: UserCheck,
    description: 'Formulaire de pré-checkin externe pour les clients',
    depends: ['reservations']
  },
  documents: {
    label: 'Documents / Fiches police',
    icon: FileText,
    description: 'Gestion des passeports et fiches de police',
    depends: ['reservations']
  },
  guests: {
    label: 'Fichier clients',
    icon: Users,
    description: 'Base de données des clients (PAX)',
    depends: ['reservations']
  },
  pos: {
    label: 'Point de vente (POS)',
    icon: ShoppingCart,
    description: 'Ventes de produits et services'
  },
  payments: {
    label: 'Encaissements',
    icon: Banknote,
    description: 'Gestion des encaissements et fond de caisse'
  },
  expenses: {
    label: 'Dépenses',
    icon: Receipt,
    description: 'Suivi des dépenses et fournisseurs'
  },
  charges: {
    label: 'Charges récurrentes',
    icon: Wallet,
    description: 'Gestion des charges fixes (loyer, électricité...)'
  },
  payouts: {
    label: 'Versements OTA',
    icon: Building2,
    description: 'Suivi des versements des plateformes',
    depends: ['reservations']
  },
  refunds: {
    label: 'Remboursements',
    icon: Undo2,
    description: 'Gestion des remboursements clients',
    depends: ['reservations', 'payments']
  },
  hr: {
    label: 'Ressources humaines',
    icon: Users,
    description: 'Gestion du personnel, pointage, congés'
  },
  reports: {
    label: 'Rapports',
    icon: BarChart3,
    description: 'Tableaux de bord et statistiques'
  },
  ical: {
    label: 'Synchronisation iCal',
    icon: Calendar,
    description: 'Import/Export calendrier avec OTA',
    depends: ['reservations', 'calendar']
  },
  backup: {
    label: 'Sauvegarde/Restauration',
    icon: Database,
    description: 'Création et restauration de sauvegardes système'
  }
};

export default function ModulesTab() {
  const { modules: currentModules, refreshModules } = useModules();
  const [localModules, setLocalModules] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalModules(currentModules);
  }, [currentModules]);

  // Check if there are unsaved changes
  useEffect(() => {
    const changed = Object.keys(localModules).some(
      key => localModules[key] !== currentModules[key]
    );
    setHasChanges(changed);
  }, [localModules, currentModules]);

  const handleToggle = (moduleKey, enabled) => {
    const moduleDef = MODULE_DEFINITIONS[moduleKey];
    
    // If disabling a module, check if other modules depend on it
    if (!enabled) {
      const dependentModules = Object.entries(MODULE_DEFINITIONS)
        .filter(([key, def]) => def.depends?.includes(moduleKey) && localModules[key])
        .map(([key, def]) => def.label);
      
      if (dependentModules.length > 0) {
        // Auto-disable dependent modules
        const newState = { ...localModules, [moduleKey]: false };
        Object.entries(MODULE_DEFINITIONS).forEach(([key, def]) => {
          if (def.depends?.includes(moduleKey)) {
            newState[key] = false;
          }
        });
        setLocalModules(newState);
        toast.info(`Modules dépendants désactivés: ${dependentModules.join(', ')}`);
        return;
      }
    }
    
    // If enabling a module, check if its dependencies are enabled
    if (enabled && moduleDef?.depends) {
      const disabledDeps = moduleDef.depends.filter(dep => !localModules[dep]);
      if (disabledDeps.length > 0) {
        // Auto-enable dependencies
        const newState = { ...localModules, [moduleKey]: true };
        disabledDeps.forEach(dep => {
          newState[dep] = true;
        });
        setLocalModules(newState);
        const depLabels = disabledDeps.map(d => MODULE_DEFINITIONS[d]?.label || d);
        toast.info(`Modules requis activés: ${depLabels.join(', ')}`);
        return;
      }
    }
    
    setLocalModules(prev => ({ ...prev, [moduleKey]: enabled }));
  };

  const saveModules = async () => {
    setSaving(true);
    try {
      await api.put('/config/singleton/MODULES', { metadata: localModules });
      await refreshModules();
      toast.success('Configuration des modules enregistrée');
      setHasChanges(false);
    } catch (error) {
      console.error('Save modules error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setLocalModules(currentModules);
    setHasChanges(false);
  };

  // Group modules by category
  const coreModules = ['reservations', 'calendar', 'precheckin', 'documents', 'guests'];
  const financeModules = ['pos', 'payments', 'expenses', 'charges', 'payouts', 'refunds'];
  const adminModules = ['hr', 'reports', 'ical', 'backup'];

  const renderModuleCard = (moduleKey) => {
    const moduleDef = MODULE_DEFINITIONS[moduleKey];
    if (!moduleDef) return null;
    
    const Icon = moduleDef.icon;
    const isEnabled = localModules[moduleKey] !== false;
    const isCritical = moduleDef.critical;
    const hasDeps = moduleDef.depends?.length > 0;
    
    return (
      <div 
        key={moduleKey}
        className={`p-4 border rounded-lg transition-all ${
          isEnabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-70'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/10' : 'bg-gray-100'}`}>
              <Icon className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium">{moduleDef.label}</p>
                {isCritical && (
                  <Badge variant="outline" className="text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    Critique
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{moduleDef.description}</p>
              {hasDeps && (
                <p className="text-xs text-muted-foreground mt-2">
                  Dépend de: {moduleDef.depends.map(d => MODULE_DEFINITIONS[d]?.label || d).join(', ')}
                </p>
              )}
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => handleToggle(moduleKey, checked)}
            disabled={isCritical}
            data-testid={`module-switch-${moduleKey}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="modules-tab">
      {/* Header with save button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <LayoutGrid className="w-5 h-5" />
            Gestion des modules
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Activez ou désactivez les fonctionnalités selon vos besoins
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <>
              <Button variant="ghost" onClick={resetChanges} size="sm">
                Annuler
              </Button>
              <Button onClick={saveModules} disabled={saving} data-testid="save-modules-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Warning about changes */}
      {hasChanges && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Modifications non enregistrées</p>
            <p className="text-sm text-amber-700">
              Les changements ne seront visibles qu'après enregistrement et rafraîchissement de la page.
            </p>
          </div>
        </div>
      )}

      {/* Core Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Modules principaux</CardTitle>
          <CardDescription>Fonctionnalités liées aux réservations et clients</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {coreModules.map(renderModuleCard)}
        </CardContent>
      </Card>

      {/* Finance Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Modules financiers</CardTitle>
          <CardDescription>Gestion de la comptabilité et des flux financiers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {financeModules.map(renderModuleCard)}
        </CardContent>
      </Card>

      {/* Admin Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Administration</CardTitle>
          <CardDescription>Outils de gestion et rapports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {adminModules.map(renderModuleCard)}
        </CardContent>
      </Card>

      {/* Info about module behavior */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Comment ça fonctionne ?</p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• Désactiver un module le retire du menu de navigation</li>
              <li>• Les pages correspondantes ne sont plus accessibles</li>
              <li>• Les données existantes sont conservées</li>
              <li>• Certains modules dépendent d'autres (indiqué sous chaque module)</li>
              <li>• Le module Réservations est critique et ne peut pas être désactivé</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
